import { useTranslation } from 'react-i18next';
import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { parse } from 'query-string';
import cx from 'clsx';

import {
  IconRcHiAccountDropdown,
  IconRcHiAccountWithdraw,
  IconRcHiAccountDeposit,
} from '@/assets/account';
import { Dropdown, HelperTooltip, Button } from '@/components';
import { CopyImage } from '@/components/shared';
import { AccountStatus } from '@/consts';
import {
  useCurrentAddressInfo,
  useSwitch,
  useUserInfo,
  useCurrentAddressAccount,
  useL2AccountStatus,
} from '@/hooks';
import { loginWallet, ProfileUSDValue, getUserNameByUserInfo } from '@/utils';

import {
  AccountRegisterModal,
  AccountRetrieveModal,
  DepositModal,
  WithdrawModal,
  DeleteKeyModal,
  TransferKeyModal,
  ResetKeyModal,
} from '../AccountModal';
import style from './index.module.css';

export const AccountInfo = ({ className }: { className?: string }) => {
  const { t } = useTranslation();
  const { state } = useLocation<{ depositTo?: { chain: string; id: string } }>() || {};

  const { address, isVerified, account } = useCurrentAddressInfo();
  const accountStatus = useL2AccountStatus();

  const { data: userInfo } = useUserInfo(address);
  const {
    on: onRegisterModal,
    turnOff: turnOffRegisterModal,
    turnOn: turnOnRegisterModal,
  } = useSwitch(false);
  const {
    on: onRetrieveModal,
    turnOff: turnOffRetrieveModal,
    turnOn: turnOnRetrieveModal,
  } = useSwitch(false);
  const {
    on: onDepositModal,
    turnOff: turnOffDepositModal,
    turnOn: turnOnDepositModal,
  } = useSwitch(false);
  const { on: onWithdraw, turnOff: turnOffWithdraw, turnOn: turnOnWithdraw } = useSwitch(false);
  const { on: isCreateKey, turnOff: turnOffCreateKey, turnOn: turnOnCreateKey } = useSwitch(false);
  const {
    on: onDeleteKeyModal,
    turnOff: turnOffDeleteKeyModal,
    turnOn: turnOnDeleteKeyModal,
  } = useSwitch(false);
  const {
    on: onTransferKeyModal,
    turnOff: turnOffTransferKeyModal,
    turnOn: turnOnTransferKeyModal,
  } = useSwitch(false);
  const {
    on: onResetKeyModal,
    turnOff: turnOffResetKeyModal,
    turnOn: turnOnResetKeyModal,
  } = useSwitch(false);

  const [isForgetPassword, setIsForgetPassword] = useState(false);

  const history = useHistory();

  const targetModal = useMemo(() => {
    const searchParams = parse(history.location.search);
    return searchParams.modal;
  }, [history.location.search]);

  const accountName = useMemo(() => getUserNameByUserInfo(userInfo, true), [userInfo]);

  const { data: myAccountInfo, ...myAccountInfoQuery } = useCurrentAddressAccount();
  useEffect(() => {
    if (!isVerified) {
      history.push('/profile');
    }
  }, [history, isVerified]);

  useEffect(() => {
    const refresh = () => {
      // 现在只用做检查是不是在 withdrawing
      // 后面需要改成 use-query 的 refetchInterval
      myAccountInfoQuery.refetch();
    };
    const timer = setInterval(refresh, 5000);
    return () => clearInterval(timer);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const turnOnForgetPasswordModal = useCallback(() => {
    setIsForgetPassword(true);
    turnOnRetrieveModal();
  }, [turnOnRetrieveModal]);

  useEffect(() => {
    const openModal = async () => {
      await loginWallet();
      switch (targetModal) {
        case 'register':
          turnOnRegisterModal();
          break;
        case 'retrieve':
          turnOnRetrieveModal();
          break;
        case 'reset':
          turnOnResetKeyModal();
          break;
        case 'forgetPassword':
          turnOnForgetPasswordModal();
          break;
        default:
          break;
      }
      history.replace('/account');
    };
    if (targetModal) {
      openModal();
    }
  }, [
    history,
    targetModal,
    turnOnForgetPasswordModal,
    turnOnRegisterModal,
    turnOnResetKeyModal,
    turnOnRetrieveModal,
  ]);

  const menus = useMemo(
    () =>
      accountStatus === AccountStatus.UNREGISTERED
        ? [
            {
              label: t('AccountInfo.1jl4p5n'),
              onClick: async () => {
                await loginWallet();
                turnOnRegisterModal();
              },
            },
          ]
        : account?.encryptedPrivateKey
          ? [
              {
                label: t('AccountInfo.1qkmg2g'),
                onClick: async () => {
                  await loginWallet();
                  turnOnForgetPasswordModal();
                },
              },
              {
                label: t('AccountInfo.1ul3qwe'),
                onClick: async () => {
                  await loginWallet();
                  turnOnResetKeyModal();
                },
              },
              {
                label: t('AccountInfo.1n5mty7'),
                onClick: turnOnTransferKeyModal,
              },
              {
                label: t('AccountInfo.1gpgzwr'),
                onClick: turnOnDeleteKeyModal,
              },
            ]
          : [
              {
                // 老板钦定名字，实际是 Retrieve key
                label: t('AccountInfo.1jl4p5n'),
                onClick: async () => {
                  await loginWallet();
                  turnOnCreateKey();
                  turnOnRetrieveModal();
                },
              },
            ],
    [
      accountStatus,
      account?.encryptedPrivateKey,
      turnOnCreateKey,
      turnOnRetrieveModal,
      turnOnRegisterModal,
      turnOnDeleteKeyModal,
      turnOnTransferKeyModal,
      turnOnForgetPasswordModal,
      turnOnResetKeyModal,
      t,
    ],
  );

  const onCloseRetrieveModal = useCallback(() => {
    turnOffRetrieveModal();
    setIsForgetPassword(false);
    turnOffCreateKey();
  }, [turnOffRetrieveModal, turnOffCreateKey]);

  const onClickDepositButton = useCallback(async () => {
    await loginWallet();
    turnOnDepositModal();
  }, [turnOnDepositModal]);

  useEffect(() => {
    if (state?.depositTo) {
      onClickDepositButton();
    }
  }, [state?.depositTo, onClickDepositButton, history]);

  const handleDepositTurnoff = useCallback(() => {
    turnOffDepositModal();
    // 关闭时清空 location.state，免得再打开还是之前的选项
    history.replace('/account');
  }, [turnOffDepositModal, history]);

  const onClickWithdrawButton = useCallback(() => {
    if (myAccountInfo?.under_withdrawing) {
      return;
    }
    turnOnWithdraw();
  }, [myAccountInfo?.under_withdrawing, turnOnWithdraw]);

  return (
    <div className={cx(style.container, className)}>
      <div className={style.header}>
        <div className={style.contentLeft}>
          <div className={cx(style.accountName)}>{accountName}</div>
          <div className={style.addressInfo}>
            <span>{address}</span>
            {address ? (
              <HelperTooltip title={t('AccountInfo.copy')} placement="top">
                <div className={style.icon}>
                  <CopyImage value={address} />
                </div>
              </HelperTooltip>
            ) : null}
          </div>
        </div>
        <div className={style.contentRight}>
          <Dropdown
            overlayClassName={style.settingsDropdown}
            getPopupContainer={dom => dom.parentElement!}
            inParent
            data={menus}
            trigger={['click']}
            placement={'bottomRight'}>
            <div className={style.settingBtn}>
              {t('AccountInfo.1kn9l19')}
              <IconRcHiAccountDropdown className={style.settingBtnIcon} />
            </div>
          </Dropdown>
        </div>
      </div>
      {myAccountInfo ? (
        <div className={style.footer}>
          <div className={style.l2Balance}>
            <div className={style.label}>{t('AccountInfo.1j0yn7m')}</div>
            <div className={style.number}>{ProfileUSDValue(myAccountInfo?.balance, 2)}</div>
          </div>
          <Button
            type="green"
            onClick={onClickDepositButton}
            className={cx(style.depositBtn, style.operationButton)}>
            <IconRcHiAccountDeposit className={style.btnIcon} />
            {t('AccountInfo.1pzi6mh')}
          </Button>
          <Button
            onClick={onClickWithdrawButton}
            type="green"
            ghost
            className={cx(style.withdrawBtn, style.operationButton)}
            disabled={myAccountInfo?.under_withdrawing}>
            <IconRcHiAccountWithdraw className={style.btnIcon} />
            {t('AccountInfo.e7jzck')}
          </Button>
        </div>
      ) : null}

      {onRegisterModal ? <AccountRegisterModal turnOff={turnOffRegisterModal} /> : null}
      {onRetrieveModal ? (
        <AccountRetrieveModal
          isForgetPassword={isForgetPassword}
          isCreateKey={isCreateKey}
          turnOff={onCloseRetrieveModal}
        />
      ) : null}
      {onDepositModal ? (
        <DepositModal turnOff={handleDepositTurnoff} depositTo={state?.depositTo} />
      ) : null}
      {onWithdraw ? <WithdrawModal turnOff={turnOffWithdraw} /> : null}
      {onDeleteKeyModal ? <DeleteKeyModal turnOff={turnOffDeleteKeyModal} /> : null}
      {onTransferKeyModal ? <TransferKeyModal turnOff={turnOffTransferKeyModal} /> : null}
      {onResetKeyModal ? <ResetKeyModal turnOff={turnOffResetKeyModal} /> : null}
    </div>
  );
};
